import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { ThemeId, DEFAULT_THEME, THEMES } from '../themes/themes';
import { authService } from '../services/auth.service';

// ── Shape ──────────────────────────────────────────────────────────────────

export interface ThemeContextValue {
  /** Currently active theme id */
  theme: ThemeId;
  /** Switch to a different theme — persists per user */
  setTheme: (id: ThemeId) => void;
  /** All available themes for building a picker UI */
  themes: typeof THEMES;
}

// ── Helpers ────────────────────────────────────────────────────────────────

/** Returns a user-scoped localStorage key so each login retains their preference. */
function resolveStorageKey(): string {
  try {
    const user = authService.getStoredUser();
    if (user?.id) return `crm_theme_u${user.id}`;
  } catch {
    // authService unavailable (unauthenticated page) — use guest key
  }
  return 'crm_theme_guest';
}

function readPersistedTheme(): ThemeId {
  try {
    const key = resolveStorageKey();
    const raw = localStorage.getItem(key);
    if (raw && THEMES.some(t => t.id === raw)) return raw as ThemeId;
  } catch {
    // localStorage unavailable (private browsing / storage full)
  }
  return DEFAULT_THEME;
}

/** Applies the theme to <html data-theme="..."> immediately. */
function applyThemeToDOM(id: ThemeId): void {
  document.documentElement.setAttribute('data-theme', id);
}

/** Applies the persisted density preference to <html data-density="..."> on bootstrap. */
function applyPersistedDensity(): void {
  try {
    const v = localStorage.getItem('crm_density');
    if (v === 'compact') document.documentElement.setAttribute('data-density', 'compact');
    else document.documentElement.removeAttribute('data-density');
  } catch { /* ignore */ }
}

// ── Context ────────────────────────────────────────────────────────────────

export const ThemeContext = createContext<ThemeContextValue>({
  theme: DEFAULT_THEME,
  setTheme: () => {},
  themes: THEMES,
});

// ── Provider ───────────────────────────────────────────────────────────────

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [theme, setThemeState] = useState<ThemeId>(() => {
    const persisted = readPersistedTheme();
    // Apply immediately (before first paint) to avoid flash
    applyThemeToDOM(persisted);
    applyPersistedDensity();
    return persisted;
  });

  // Re-apply whenever theme changes (e.g. user switched mid-session)
  useEffect(() => {
    applyThemeToDOM(theme);
  }, [theme]);

  // Cross-tab sync: another tab changed the theme
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      const key = resolveStorageKey();
      if (e.key === key && e.newValue && THEMES.some(t => t.id === e.newValue)) {
        const next = e.newValue as ThemeId;
        setThemeState(next);
        applyThemeToDOM(next);
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  // Re-read persisted theme when user logs in/out (auth change event)
  useEffect(() => {
    const handler = () => {
      const next = readPersistedTheme();
      setThemeState(next);
      applyThemeToDOM(next);
    };
    window.addEventListener('auth-changed', handler);
    return () => window.removeEventListener('auth-changed', handler);
  }, []);

  const setTheme = useCallback((id: ThemeId) => {
    if (!THEMES.some(t => t.id === id)) return; // guard against invalid ids
    setThemeState(id);
    applyThemeToDOM(id);
    try {
      localStorage.setItem(resolveStorageKey(), id);
    } catch {
      // Storage full or unavailable — theme still applied for current session
    }
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, setTheme, themes: THEMES }),
    [theme, setTheme]
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};
